import { toBcp47 } from '@shared/dates'
import type { TimeFormatPref } from '@shared/preferences'

export type HourCycle = '12h' | '24h'

const clockFormatterCache = new Map<string, Intl.DateTimeFormat>()
// Pinned rather than read from Intl: CLDR gives es-CL a 12h clock, but festival
// timetables in Chile are read in 24h.
const LOCALE_CLOCK: Record<string, HourCycle> = { en_US: '12h', es_CL: '24h' }

/** The app language's default clock: en_US → 12h, es_CL → 24h. */
export function localeHourCycle(locale: string): HourCycle {
  const pinned = LOCALE_CLOCK[locale]
  if (pinned) return pinned
  const resolved = new Intl.DateTimeFormat(toBcp47(locale), { hour: 'numeric' }).resolvedOptions().hourCycle
  return resolved === 'h11' || resolved === 'h12' ? '12h' : '24h'
}

/**
 * The user's pick wins; until there is one, the phone's 12/24-hour setting
 * (native app only — `device`), then the app language.
 */
export function resolveTimeFormat(pref: TimeFormatPref, locale: string, device: HourCycle | null = null): HourCycle {
  if (pref !== 'auto') return pref
  return device ?? localeHourCycle(locale)
}

function clockFormatter12(locale: string): Intl.DateTimeFormat {
  let fmt = clockFormatterCache.get(locale)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(toBcp47(locale), { hour: 'numeric', minute: '2-digit', hour12: true })
    clockFormatterCache.set(locale, fmt)
  }
  return fmt
}

function parseHM(hhmm: string): [number, number] | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  return h < 24 && m < 60 ? [h, m] : null
}

/** Display an "HH:mm" set time. '24h' returns it unchanged; '12h' → "9:30 PM" / "9:30 p. m.". */
export function formatClockTime(hhmm: string, cycle: HourCycle, locale: string): string {
  if (cycle === '24h') return hhmm
  const parts = parseHM(hhmm)
  if (!parts) return hhmm
  return clockFormatter12(locale).format(new Date(2000, 0, 1, parts[0], parts[1]))
}

/**
 * A set's "start – end". In 12h only the end carries AM/PM ("11:00 – 12:00 AM"),
 * so it fits narrow grid columns; the start's is obvious from the grid.
 */
export function formatClockRange(start: string, end: string, cycle: HourCycle, locale: string): string {
  if (cycle === '24h') return `${start} – ${end}`
  // Every set block formats its range on every render; Intl is slow enough to
  // show up in the schedule's profile, and a festival has few distinct times.
  const key = `${locale}|${start}|${end}`
  let range = rangeCache.get(key)
  if (range === undefined) {
    if (rangeCache.size > 5000) rangeCache.clear()
    range = formatClockRange12(start, end, locale)
    rangeCache.set(key, range)
  }
  return range
}

const rangeCache = new Map<string, string>()

function formatClockRange12(start: string, end: string, locale: string): string {
  const cycle: HourCycle = '12h'
  const a = parseHM(start)
  const b = parseHM(end)
  if (!a || !b) {
    return `${formatClockTime(start, cycle, locale)} – ${formatClockTime(end, cycle, locale)}`
  }
  const startTime = clockFormatter12(locale)
    .formatToParts(new Date(2000, 0, 1, a[0], a[1]))
    .filter(p => p.type !== 'dayPeriod')
    .map(p => p.value)
    .join('')
    .trim()
  return `${startTime} – ${formatClockTime(end, cycle, locale)}`
}
